import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
  ChainlitContext,
  IStarter,
  IStep,
  modesState,
  useAuth,
  useChatData,
  useChatInteract
} from '@chainlit/react-client';

import { Button } from '@/components/ui/button';

import { persistentCommandState } from '@/state/chat';

import BibleSummaryDialog from './dialogs/BibleSummaryDialog';
import MakeWorksheetDialog from './dialogs/MakeWorksheetDialog';
import SendMessageDialog from './dialogs/SendMessageDialog';

interface StarterProps {
  starter: IStarter;
}

const BIBLE_SUMMARY_MESSAGE = 'Summarize a Bible chapter';
const SEND_MESSAGE_MESSAGE = 'Send a message';
const MAKE_WORKSHEET_MESSAGE = 'Generate a worksheet';

export default function Starter({ starter }: StarterProps) {
  const apiClient = useContext(ChainlitContext);
  const selectedCommand = useRecoilValue(persistentCommandState);
  const modes = useRecoilValue(modesState);
  const { sendMessage } = useChatInteract();
  const { loading, connected } = useChatData();
  const { user } = useAuth();

  const [bibleOpen, setBibleOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [worksheetOpen, setWorksheetOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Deep-link: honda-ai.com/worksheet opens the Make Worksheet dialog once
  // the welcome screen mounts. AppWrapper + Login + AuthCallback preserve the
  // /worksheet URL across OAuth, so on arrival the pathname is always right.
  useEffect(() => {
    if (
      starter.message === MAKE_WORKSHEET_MESSAGE &&
      location.pathname === '/worksheet'
    ) {
      setWorksheetOpen(true);
      navigate('/', { replace: true });
    }
  }, [starter.message, location.pathname, navigate]);

  const disabled = loading || !connected;

  const dispatchMessage = useCallback(
    (output: string, fileReferences: { id: string }[] = []) => {
      // Build modes dict: only include modes that have selections
      // (same logic as MessageComposer)
      const modesDict: Record<string, string> = {};
      modes.forEach((mode) => {
        const defaultOpt = mode.options.find((opt) => opt.default);
        const selectedId = defaultOpt?.id || mode.options[0]?.id;
        if (selectedId) {
          modesDict[mode.id] = selectedId;
        }
      });

      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        command: starter.command ?? selectedCommand?.id,
        modes: Object.keys(modesDict).length > 0 ? modesDict : undefined,
        name: user?.identifier || 'User',
        type: 'user_message',
        output,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      sendMessage(message, fileReferences);
    },
    [user, selectedCommand, modes, sendMessage, starter]
  );

  const onClick = useCallback(() => {
    if (starter.message === BIBLE_SUMMARY_MESSAGE) {
      setBibleOpen(true);
      return;
    }
    if (starter.message === SEND_MESSAGE_MESSAGE) {
      setSendOpen(true);
      return;
    }
    if (starter.message === MAKE_WORKSHEET_MESSAGE) {
      setWorksheetOpen(true);
      return;
    }
    dispatchMessage(starter.message);
  }, [starter, dispatchMessage]);

  return (
    <>
      <Button
        id={`starter-${starter.label.trim().toLowerCase().replaceAll(' ', '-')}`}
        variant="outline"
        className="w-fit justify-start rounded-3xl"
        disabled={disabled}
        onClick={onClick}
      >
        <div className="flex gap-2">
          {starter.icon ? (
            <img
              className="h-5 w-5 rounded-md"
              src={
                starter.icon?.startsWith('/public')
                  ? apiClient.buildEndpoint(starter.icon)
                  : starter.icon
              }
              alt={starter.label}
            />
          ) : null}
          <p className="text-sm text-muted-foreground truncate">
            {starter.label}
          </p>
        </div>
      </Button>
      {starter.message === BIBLE_SUMMARY_MESSAGE && (
        <BibleSummaryDialog
          open={bibleOpen}
          onOpenChange={setBibleOpen}
          onSubmit={dispatchMessage}
        />
      )}
      {starter.message === SEND_MESSAGE_MESSAGE && (
        <SendMessageDialog
          open={sendOpen}
          onOpenChange={setSendOpen}
          onSubmit={dispatchMessage}
        />
      )}
      {starter.message === MAKE_WORKSHEET_MESSAGE && (
        <MakeWorksheetDialog
          open={worksheetOpen}
          onOpenChange={setWorksheetOpen}
          onSubmit={dispatchMessage}
        />
      )}
    </>
  );
}
